import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { WarningBanner } from 'ui/banner';
import { Button, BUTTON_SIZES, BUTTON_VARIANTS } from 'ui/button';
import { FormField } from 'ui/formField';
import { InputText } from 'ui/inputs';
import { HelpToggletip, TipPlacement } from 'ui/tooltip';
import { Toggle } from 'ui/toggle';
import { Modal, MODAL_SIZES, MODAL_VARIANTS, ModalFooter, ModalHeader, ModalBody } from 'ui/modal';
import { RadioList } from 'ui/radioList';
import { colors } from 'ui/theme';

import { RowWrapper } from 'modules/workflowNodeEditors/EditorBody.styled';
import { parseNodeForSaving } from 'modules/workflows/utils';
import { selectEmail, selectUuidProjectType, UuidProjectType } from 'core/userContext';
import { EMAIL_NON_UNIQUE_PROJECT } from 'core/userContext/constants';

import {
  closeModal,
  sendTestTriggerRequest,
  getNodeById,
  getCompleteWorkflow,
  WorkflowsRootState,
} from 'modules/workflows/ducks';
import { SEND_TEST_TRIGGER_MODAL } from '../../../constants';
import {
  ToggleWrapper,
  StyledAceEditor,
  ModalToggleLabel,
  WarningMessageWrapper,
  AceEditorWrapper,
} from './SendTestTriggerModal.styled';

type Props = {
  /** Whether modal is open and visible */
  isOpen: boolean;
};

export const content = {
  title: 'Send a proof of this journey',
  label: 'Simulate journey for',
  warningMessage: `Warning: It's best to send test messages to someone
  within your organization (like yourself). If you select one of
  your customers, they will start receiving messages from this journey.`,
  submitButton: 'Test journey',
  toggleText: 'Add test data',
  tooltipContent: `You can add event data or contact properties (in JSON format) to simulate
  the conditions of your real journey.`,
  editorContent:
    'Example: If your journey is triggered when a customer \nmakes a purchase of $100, you could enter: \n { \n   "ChargeAmount":100 \n }',
};

export const StyledRadioList = styled(RadioList)`
  > div {
    margin-bottom: 0;
  }
`;

export const SendTestTriggerModal = ({ isOpen }: Props) => {
  const workflow = useSelector(getCompleteWorkflow);
  const workflowId = Number(workflow.id);
  const recipientEmail = useSelector(selectEmail) || '';

  const { startWorkflowNodeId } = workflow;
  const triggerNode = useSelector((state: WorkflowsRootState) =>
    getNodeById(state, `${startWorkflowNodeId}`),
  );

  const uuidProjectType = useSelector(selectUuidProjectType) || UuidProjectType.EmailUnique;
  const [recipient, setRecipient] = useState(recipientEmail);
  const [proofRecipientType, setProofRecipientType] = useState('email');
  const [areJSONDataFieldsEnabled, setAreJSONDataFieldsEnabled] = useState(false);
  const [dataFields, setDataFields] = useState();

  useEffect(() => {
    if (proofRecipientType === 'email') {
      // set default email
      setRecipient(recipientEmail);
    } else {
      // set default userId
      // temporarily using email here because we can't fetch userId yet
      setRecipient(recipientEmail);
    }
  }, [proofRecipientType]);

  /* Change Handlers */
  const dispatch = useDispatch();

  const closeEditor = () => {
    dispatch(closeModal(SEND_TEST_TRIGGER_MODAL));
  };

  const sendTestTrigger = () => {
    const isSendLimitOverrideEnabled = true;

    dispatch(
      sendTestTriggerRequest(
        JSON.parse(dataFields || JSON.stringify({})),
        proofRecipientType === 'email' ? recipient : null,
        proofRecipientType === 'userId' ? recipient : null,
        workflow,
        parseNodeForSaving(triggerNode, workflowId),
        isSendLimitOverrideEnabled,
      ),
    );
    closeEditor();
  };

  return (
    <Modal
      id={SEND_TEST_TRIGGER_MODAL}
      isModalOpen={isOpen}
      onClose={closeEditor}
      size={MODAL_SIZES.MEDIUM}
      variant={MODAL_VARIANTS.DEFAULT}
    >
      <ModalHeader>{content.title}</ModalHeader>
      <ModalBody>
        <FormField labelText={content.label} inputId="email-recipient-field">
          {uuidProjectType === EMAIL_NON_UNIQUE_PROJECT && (
            <StyledRadioList
              name="proof-recipient-type"
              onSelect={setProofRecipientType}
              options={['email', 'userId'].map((key) => ({ id: key, name: key, value: key }))}
              value={proofRecipientType}
            />
          )}
          <InputText id="recipient-input" onChange={setRecipient} type="text" value={recipient} />
        </FormField>
        <WarningMessageWrapper>
          <WarningBanner Icon={false}>{content.warningMessage}</WarningBanner>
        </WarningMessageWrapper>
        <ToggleWrapper>
          <RowWrapper>
            <Toggle
              id="json-data-fields-toggle"
              isActive={areJSONDataFieldsEnabled}
              labelId="json-data-fields-label"
              onClick={() => setAreJSONDataFieldsEnabled(!areJSONDataFieldsEnabled)}
            />
            <ModalToggleLabel id="json-data-fields-label" htmlFor="json-data-fields-toggle">
              {content.toggleText}
            </ModalToggleLabel>
            <HelpToggletip
              placement={TipPlacement.Right}
              tooltipContent={content.tooltipContent}
              iconProps={{ color: colors.secondaryText }}
            />
          </RowWrapper>
          {areJSONDataFieldsEnabled && (
            <AceEditorWrapper>
              <StyledAceEditor
                height="152px"
                mode="json"
                onChange={(value) => setDataFields(value)}
                wrapEnabled={true}
                value={dataFields}
                placeholder={content.editorContent}
              />
            </AceEditorWrapper>
          )}
        </ToggleWrapper>
      </ModalBody>
      <ModalFooter>
        <Button
          autoFocus
          onClick={sendTestTrigger}
          size={BUTTON_SIZES.LARGE}
          variant={BUTTON_VARIANTS.PRIMARY}
        >
          {content.submitButton}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
